import React from 'react'
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalActions,
  Button,
  Icon,
  Header,
  Grid,
  Segment,
  Label,
  List,
  Table,
  Message,
  Transition,
} from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import {
  DELETE_KEG_COLLECTION,
  RESET_DELETE_KEG_COLLECTION_STATUS_STATE,
  SHOW_BOOKINGS_MODAL_ERROR_MESSAGE,
  HIDE_BOOKINGS_MODAL_ERROR_MESSAGE,
  CLOSE_BOOKING_AFTER_KEG_COLLECTION_DELETION,
} from '../../../../../../redux/actions/actionTypes'
import { hourToLocalizedLabel, DAY_OPTIONS } from '../../../utils/kegCollectionFormUtils'
import ModalErrorBanner from '../../shared/BookingsModalErrorBanner/index'

const KegCollectionDeleteModal = props => {
  const {
    dispatch,
    createAction,
    kegCollectionObj,
    kegCollectionQuantities,
    formatDate,
    dateFormatter,
    deleteKegCollectionRequestStatusState: { isDeleting, didDeleteSucceed, didDeleteFail },
    bookingData: { content },
  } = props

  const [openDeleteModal, setOpenDeleteModal] = React.useState(false)
  const {
    i18n: { locale },
    t: translate,
  } = useTranslation()

  React.useEffect(() => {
    if (didDeleteSucceed) {
      setOpenDeleteModal(false)
    }
  }, [didDeleteSucceed, dispatch, createAction, translate])

  React.useEffect(() => {
    if (didDeleteFail) {
      dispatch(
        createAction(SHOW_BOOKINGS_MODAL_ERROR_MESSAGE, {
          header: translate('Error'),
          content: translate('kegCollectionDeleteErrorMessage'),
        })
      )
    }
  }, [didDeleteFail, dispatch, createAction, translate])

  const [mounted, setMounted] = React.useState(false)
  // eslint-disable-next-line consistent-return
  React.useEffect(() => {
    if (openDeleteModal) {
      const t = setTimeout(() => setMounted(true), 80)
      return () => clearTimeout(t)
    }
    setMounted(false)
  }, [openDeleteModal])

  const availableDays = React.useMemo(() => {
    if (!kegCollectionObj) return {}
    return {
      monday: !!kegCollectionObj?.venueOpenMonday,
      tuesday: !!kegCollectionObj?.venueOpenTuesday,
      wednesday: !!kegCollectionObj?.venueOpenWednesday,
      thursday: !!kegCollectionObj?.venueOpenThursday,
      friday: !!kegCollectionObj?.venueOpenFriday,
    }
  }, [kegCollectionObj])

  const handleDelete = React.useCallback(() => {
    if (!kegCollectionObj?.id) return
    if (content?.kegCollectionRequestObjects?.length === 1) {
      // Close the booking after deleting last remaining keg collection request
      const bookingDuplicate = {
        ...(content?.bookingRawData ? content.bookingRawData : content),
        bookingStatus: 'CANCELLED',
      }
      dispatch(
        createAction(CLOSE_BOOKING_AFTER_KEG_COLLECTION_DELETION, {
          kegCollectionId: kegCollectionObj.id,
          bookingData: bookingDuplicate,
        })
      )
    } else {
      dispatch(
        createAction(DELETE_KEG_COLLECTION, {
          kegCollectionId: kegCollectionObj.id,
        })
      )
    }
  }, [dispatch, createAction, kegCollectionObj, content])

  const quantities = [
    { id: 'L20', label: '20L' },
    { id: 'L30', label: '30L' },
    { id: 'G9', label: '9G' },
    { id: 'L50', label: '50L' },
  ]

  const dayChips = DAY_OPTIONS.filter(d => availableDays[d])

  return (
    <Modal
      open={openDeleteModal}
      onOpen={() => setOpenDeleteModal(true)}
      onClose={() => setOpenDeleteModal(false)}
      trigger={
        <button type="button" className="kegCollectionTableRowBtn kegCollectionBtnDelete">
          <Icon name="trash" style={{ marginBottom: 3 }} /> {translate('Delete')}
        </button>
      }
      size="small"
      role="dialog"
      aria-modal="true"
      aria-labelledby="keg-delete-header"
      closeOnDimmerClick={false}
      closeOnEscape
    >
      <ModalErrorBanner />
      <ModalHeader id="keg-delete-header">
        {translate('deleteKegCollectionModalHeader')}
      </ModalHeader>
      <Transition visible={mounted} animation="fade up" duration={300}>
        <ModalContent scrolling>
          <Message warning icon>
            <Icon name="warning circle" />
            {content?.kegCollectionRequestObjects?.length === 1 ? (
              <Message.Content>
                <Message.Header>{translate('closeThisBookingHeader')}</Message.Header>
                {translate('closeThisBookingContent')}
              </Message.Content>
            ) : (
              <Message.Content>
                <Message.Header>{translate('actionCannotBeUndone')}</Message.Header>
                {translate('deleteKegCollectionDeleteNotice')}
              </Message.Content>
            )}
          </Message>

          <Grid stackable columns={2}>
            <Grid.Column>
              <Segment>
                <Transition visible={mounted} animation="fade" duration={250}>
                  <Header as="h4" content={kegCollectionObj?.customerName || '—'} />
                </Transition>

                <List relaxed>
                  {kegCollectionObj?.fullAddress && (
                    <List.Item>
                      <Icon name="map marker alternate" color="grey" />
                      <List.Content>
                        <strong>{kegCollectionObj.fullAddress}</strong>
                      </List.Content>
                    </List.Item>
                  )}

                  {kegCollectionObj?.dateCreated && (
                    <List.Item>
                      <Icon name="calendar alternate outline" color="grey" />
                      <List.Content>
                        <strong>{translate('Created')}:</strong>{' '}
                        {formatDate
                          ? formatDate(kegCollectionObj.dateCreated, dateFormatter, true)
                          : kegCollectionObj.dateCreated}
                      </List.Content>
                    </List.Item>
                  )}
                </List>
              </Segment>

              <Segment>
                <Header as="h5" content={translate('collectionWindow')} />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  <Label basic size="large">
                    <Icon name="clock outline" />
                    {translate('Start')}:&nbsp;
                    {hourToLocalizedLabel(kegCollectionObj?.startHour, { locale })}
                  </Label>
                  <Label basic size="large">
                    <Icon name="clock" />
                    {translate('End')}:&nbsp;
                    {hourToLocalizedLabel(kegCollectionObj?.endHour, { locale })}
                  </Label>
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {dayChips.length > 0 ? (
                    dayChips.map(d => (
                      <Label key={d} color="blue" basic>
                        {translate(d.charAt(0).toUpperCase() + d.slice(1))}
                      </Label>
                    ))
                  ) : (
                    <Label basic>{translate('noWeekdaysSelected')}</Label>
                  )}
                </div>
              </Segment>

              <Segment>
                <Header as="h5" content={translate('kegLocation')} />
                <Label color="teal" basic>
                  <Icon name="building" />
                  {translate(kegCollectionObj?.kegLocation) || '—'}
                </Label>
              </Segment>

              {kegCollectionObj?.pickupInstruction && (
                <Segment>
                  <Header as="h5" content={translate('pickupInstructions')} />
                  <div
                    style={{
                      whiteSpace: 'pre-wrap',
                      color: 'rgba(0,0,0,.8)',
                    }}
                  >
                    {kegCollectionObj.pickupInstruction}
                  </div>
                </Segment>
              )}

              {kegCollectionObj?.phoneNumber && (
                <Segment>
                  <Header as="h5" content={translate('contactPhoneNumber')} />
                  <List>
                    <List.Item>
                      <Icon name="phone" />
                      <List.Content>{kegCollectionObj.phoneNumber}</List.Content>
                    </List.Item>
                  </List>
                </Segment>
              )}
            </Grid.Column>

            <Grid.Column>
              <Segment>
                <Header as="h5" content={translate('kegQuantities')} />
                <Table basic="very" compact="very" celled>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>{translate('kegSize')}</Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">{translate('Quantity')}</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {quantities.map(({ id, label }) => (
                      <Table.Row key={id}>
                        <Table.Cell>{label}</Table.Cell>
                        <Table.Cell textAlign="right">
                          <Label circular>{kegCollectionQuantities?.[id] ?? 0}</Label>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </Segment>
            </Grid.Column>
          </Grid>
        </ModalContent>
      </Transition>

      <ModalActions>
        <Button
          onClick={() => {
            setOpenDeleteModal(false)
            dispatch(createAction(RESET_DELETE_KEG_COLLECTION_STATUS_STATE))
            dispatch(createAction(HIDE_BOOKINGS_MODAL_ERROR_MESSAGE))
          }}
          disabled={isDeleting}
        >
          <Icon name="times" /> {translate('Cancel')}
        </Button>
        <Button negative onClick={handleDelete} loading={isDeleting} disabled={isDeleting}>
          <Icon name="trash" /> {translate('Delete')}
        </Button>
      </ModalActions>
    </Modal>
  )
}

export default KegCollectionDeleteModal
